import React, { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, Ban, Loader2, Merge } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { MergePreview, duplicatesApi } from '../../../api/duplicates';
import { UserSummaryCard, apiErrorMessage, getErrorCode, userTitle } from './shared';

// Коды причин, по которым backend запрещает склейку (merge-preview.blockReason и 409 code)
const BLOCK_REASON_TEXT: Record<string, string> = {
  DUPLICATE_HAS_PAYMENTS: 'У дубля есть оплаты или подписка. Решите вручную: возврат или продление.',
  SAME_USER: 'Выбран один и тот же аккаунт.',
  DUPLICATE_DELETED: 'Дубль удалён.',
  MAIN_DELETED: 'Основной аккаунт удалён.',
  DUPLICATE_MERGED: 'Дубль уже склеен.',
  MAIN_MERGED: 'Основной аккаунт сам склеен в другой — выберите тот, в который он склеен.',
  DUPLICATE_NO_TELEGRAM: 'У дубля нет Telegram — переносить нечего.',
  MAIN_HAS_OTHER_TELEGRAM: 'К основному аккаунту уже привязан другой Telegram.',
};

const reasonText = (code: string | null | undefined, fallback: string) =>
  (code && BLOCK_REASON_TEXT[code]) || fallback;

export interface MergeTarget {
  duplicateId: string;
  mainId: string;
}

interface MergeDialogProps {
  isDark: boolean;
  target: MergeTarget | null;
  onClose: () => void;
  onMerged: () => void;
}

/**
 * Окно подтверждения склейки дубля: грузит merge-preview, показывает сводку
 * последствий и только потом даёт нажать «Склеить».
 */
export function MergeDialog({ isDark, target, onClose, onMerged }: MergeDialogProps) {
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // 409 DUPLICATE_HAS_PAYMENTS мог прийти уже на POST (оплата появилась после превью)
  const [mergeBlocked, setMergeBlocked] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    let cancelled = false;
    setPreview(null);
    setLoadError(null);
    setMergeBlocked(null);
    setLoading(true);
    duplicatesApi
      .mergePreview(target.duplicateId, target.mainId)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(apiErrorMessage(err, 'Не удалось загрузить сводку склейки'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target]);

  const handleConfirm = async () => {
    if (!target || !preview?.canMerge) return;
    setSubmitting(true);
    try {
      await duplicatesApi.merge(target.duplicateId, target.mainId);
      toast.success('Дубль склеен с основным аккаунтом');
      onMerged();
      onClose();
    } catch (err: any) {
      const msg = reasonText(getErrorCode(err), apiErrorMessage(err, 'Не удалось выполнить склейку'));
      if (getErrorCode(err) === 'DUPLICATE_HAS_PAYMENTS' || err?.response?.status === 409) {
        setMergeBlocked(msg);
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const strong = isDark ? 'text-white' : 'text-[#111111]';
  const blockReason = mergeBlocked ?? (preview && !preview.canMerge ? reasonText(preview.blockReason, preview.blockReason || 'Склейка невозможна') : null);
  const canConfirm = !!preview?.canMerge && !mergeBlocked && !submitting;

  const tg = preview
    ? preview.willMove.telegramUsername
      ? `@${preview.willMove.telegramUsername}`
      : preview.willMove.telegramId
      ? `id ${preview.willMove.telegramId}`
      : null
    : null;

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent
        className={cn(
          'max-w-3xl max-h-[90vh] overflow-y-auto',
          isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white',
        )}
      >
        <DialogHeader>
          <DialogTitle className={strong}>Склейка дубля</DialogTitle>
          <DialogDescription className={muted}>
            Проверьте, что это один и тот же человек, и прочитайте, что произойдёт.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
          </div>
        )}

        {loadError && !loading && (
          <div className="rounded-xl border-2 border-red-500/40 bg-red-500/5 p-4 text-sm text-red-500">
            {loadError}
          </div>
        )}

        {preview && !loading && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
              <UserSummaryCard user={preview.duplicate} role="Дубль (Telegram)" tone="duplicate" isDark={isDark} />
              <div className="flex items-center justify-center">
                <ArrowRight className={cn('h-6 w-6 rotate-90 md:rotate-0', muted)} />
              </div>
              <UserSummaryCard user={preview.main} role="Основной" tone="main" isDark={isDark} />
            </div>

            <div className={cn('rounded-xl p-4 space-y-3 text-sm', isDark ? 'bg-[#0F0F17]' : 'bg-[#F5F3EF]')}>
              <div>
                <p className={cn('font-semibold', strong)}>Основной «{userTitle(preview.main)}»</p>
                <p className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                  Получит вход через Telegram{tg ? <> <b>{tg}</b></> : null}
                  {preview.willMove.phone ? (
                    <> и номер <b>{preview.willMove.phone}</b></>
                  ) : null}
                  . Премиум, оплаты и прогресс основного не меняются.
                </p>
              </div>
              <div>
                <p className={cn('font-semibold', strong)}>Дубль «{userTitle(preview.duplicate)}»</p>
                <p className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                  Станет «склеен»: вход в него закроется, активные сессии будут отозваны. Аккаунт не
                  удаляется — его прогресс останется в архиве на дубле и <b>не переносится</b> на
                  основной.
                </p>
              </div>
            </div>

            {blockReason ? (
              <div className="flex gap-3 rounded-xl border-2 border-red-500/40 bg-red-500/5 p-4 text-sm">
                <Ban className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-500">Склейка невозможна</p>
                  <p className={isDark ? 'text-slate-300' : 'text-slate-700'}>{blockReason}</p>
                  <p className={cn('mt-1', muted)}>
                    Решите вручную: например, если дубль платил — сделайте возврат или продлите
                    премиум на основном аккаунте.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex gap-3 rounded-xl border-2 border-amber-500/40 bg-amber-500/5 p-4 text-sm">
                <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <p className={isDark ? 'text-slate-300' : 'text-slate-700'}>
                  Действие <b>не отменяется кнопкой</b>. Если ошиблись — исправлять придётся вручную
                  в базе. Действие запишется в аудит-лог.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className={cn(isDark ? 'bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white' : '')}
          >
            Отмена
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="bg-red-600 hover:bg-red-600/90 text-white"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Merge className="h-4 w-4 mr-2" />}
            Склеить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
